import React, { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { NextPage, GetServerSideProps } from 'next';
import StyledLink from '@makeship/core/lib/components/Link';
import Link from 'next/link';
import Image from 'next/image';
import { scroller } from 'react-scroll';

import posthog from 'posthog-js';
import Cookies from 'js-cookie';
import Container from '../../components/Container';
import Head from '../../components/Head';
import { H2, H4Styles, P1, P2, P2Styles, S1, H3Styles } from '../../components/Typography';
import ProductsDisplay from '../../components/ProductsDisplay';
import Sidebar from '../../components/Sidebar';

import { getCollectionByHandle } from '../../api/collections';
import { getSideBarItems } from '../../api/storefront/product';

import {
  getEndingSoonProducts,
  getNewArrivalProducts,
  getNextPage,
  getCollectionProducts,
  filterProducts,
  filterSuccessfulProducts,
  isPlushieCollectionHandle,
  isApparelCollectionHandle,
  isTopPicksCollectionHandle,
} from '../../utils/collections';
import config from '../../../config.json';
import { defaultSidebarItems } from '../../utils/sidebar';
import { CookieConsent } from '../../types/cookies';
import { getPropsForPageLoad } from '../../utils/pageLoad';
import { logSentryError } from '../../utils/errors';
import { ExperimentVariant, getExperimentFlags } from '../../context/flagging';
import SubcategorySidebar from '../../components/Sidebar/SubcategorySidebar';
import { useWindowSize } from '../../hooks/useWindowSize';
import { isUserLoggedIn } from '../../utils/analytics';
import { useStore } from '../../store';
import { experimentKeys } from '../../utils/flagging';
import { isReadyToShip } from '../../components/pages/products/commonFunctions';

const { collections, pagination } = config;

export const ShopPageWrapper = styled(Container)`
  padding: 48px 64px;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    padding: 24px 16px;
  }
`;

const ShopPageWrapperTop = styled(Container)`
  padding: 24px 16px;
  padding-bottom: 0;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    padding: 48px 64px;
    padding-bottom: 0;
  }
`;

const ShopPageWrapperBottom = styled(Container)`
  padding: 24px 16px;
  padding-top: 0;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    padding: 48px 64px;
    padding-top: 0;
  }
`;

export const ShopPageContents = styled.div<{ isCollectionsSidebarExperimentEnabled: boolean }>`
  display: flex;
  justify-content: flex-start;
  margin-top: ${({ isCollectionsSidebarExperimentEnabled }) =>
    isCollectionsSidebarExperimentEnabled ? `52px` : `64px`};
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    flex-direction: column;
    margin-top: 18px;
  }
`;

export const TitleTextNew = styled(H2)`
  color: ${({ theme }) => theme.colors.greenNewTag};
  display: inline;
`;

const ShopPageH2 = styled(H2)`
  text-transform: uppercase;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    ${H4Styles}
  }
`;

const CollectionHeaderVariantWrapper = styled.div`
  margin-bottom: 24px;
`;

const CollectionHeaderVariant = styled(S1)`
  display: inline;
  text-transform: caplitalize;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    ${H3Styles}
  }
`;

const PetitionCollectionH2 = styled(H2)`
  display: inline;
  ${ShopPageH2}
`;

const ShopPageP1 = styled(P1)`
  width: 55%;
  margin-top: 8px;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    width: 90%;
    margin-bottom: 16px;
  }
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    ${P2Styles}
  }
`;

const PetitionsHeaderInfoWrapper = styled.div`
  margin-bottom: 16px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    margin-bottom: 0px;
  }
`;

const PetitionsShopPageP1 = styled(P1)`
  margin-bottom: 8px;
  ${ShopPageP1}
`;

const PetitionsFAQLink = styled(Link)``;

const PetitionsFAQLinkText = styled(StyledLink.Primary)``;

const HideFiltersRow = styled.div`
  display: flex;
  justify-content: flex-end;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    display: none;
  }
`;

const HideFilters = styled.div`
  display: flex;
  cursor: pointer;
`;

const HideFiltersText = styled(P2)`
  margin-right: 8px;
  &:hover {
    text-decoration: underline;
  }
`;

const SubcategorySidebarWrapper = styled.div`
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    margin-top: 10px;
  }
`;

const defaultSubheading = `Fueled by the Community, you help bring the products designed by your favorite Creators to life, by funding the
Campaign within the 21 days.`;

type ShopPageProps = {
  title: string;
  handle: string;
  products: Shopify.ProductEdge[];
  collection?: Shopify.Collection;
  sidebarItems: SidebarItemsType[];
  referringURL?: string;
};

const ShopPage: NextPage<ShopPageProps> = ({
  title,
  handle,
  products,
  collection,
  sidebarItems,
  referringURL,
}: ShopPageProps) => {
  const { state } = useStore();

  const [pageProducts, setPageProducts] = useState<Shopify.ProductEdge[]>([]);
  const [initialPageProductsLoaded, setInitialPageProductsLoaded] = useState<boolean>(false);
  const [canLoadMore, setCanLoadMore] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [showSidebar, setShowSidebar] = useState<boolean>(true);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [isMaxContentViewport, setIsMaxContentViewport] = useState(false);

  const size = useWindowSize();
  const theme = useTheme();

  useEffect(() => {
    if (!size.width) {
      return;
    }
    // eslint-disable-next-line react/destructuring-assignment
    const isMobileViewport = size.width <= theme.breakpoints.largeTablet;
    setIsMobileViewport(isMobileViewport);

    const isMaxContentViewport = size.width >= theme.breakpoints.maxContent;
    setIsMaxContentViewport(isMaxContentViewport);
  }, [size]);

  const experimentFlags = getExperimentFlags();
  const isCollectionsSidebarExperimentEnabled =
    experimentFlags[experimentKeys.collectionsSidebarExperiment] === ExperimentVariant.Variant1;

  useEffect(() => {
    /* eslint-disable @typescript-eslint/no-explicit-any */
    if ((window as any).drift && (window as any).drift.api) {
      (window as any).drift.api.widget.hide();
    }
    return () => {
      if ((window as any).drift && (window as any).drift.api) {
        (window as any).drift.api.widget.show();
      }
    };
    /* eslint-enable @typescript-eslint/no-explicit-any */
  }, []);

  useEffect(() => {
    // Used for saved cursor load and scrolling
    const previousProducts = sessionStorage.getItem('products');
    const visitedProductCard = sessionStorage.getItem('visitedProductCard');

    if (
      previousProducts &&
      visitedProductCard &&
      referringURL &&
      collection &&
      referringURL.includes(collection?.handle) &&
      // no collection save behaviour for petition collection
      !referringURL.includes('/shop/petitions')
    ) {
      const productList = JSON.parse(previousProducts);
      setPageProducts(productList);
      setCanLoadMore(collection.products.edges.length === pagination.products);
      setInitialPageProductsLoaded(true);
    } else {
      setPageProducts(products);
      setCanLoadMore((collection?.products.edges.length || products.length) === pagination.products);
    }
  }, [products]);

  // Scroll to previously visited card logic
  useEffect(() => {
    const visitedProductCard = sessionStorage.getItem('visitedProductCard');
    if (visitedProductCard) {
      scroller.scrollTo(visitedProductCard, {
        duration: 800,
        smooth: 'easeInOutQuad',
        offset: -425,
      });
    }

    // After scrolling, remove product list because it has already been used (ie: it's a stale list)
    sessionStorage.removeItem('products');
  }, [initialPageProductsLoaded]);

  // Save page products into session storage as they're loaded
  useEffect(
    () => () => {
      if (pageProducts[pageProducts.length - 1]?.cursor) {
        const cookieConsentAccepted = Cookies.get(CookieConsent.Functional) === 'true';
        if (!cookieConsentAccepted) return;
        sessionStorage.setItem('collectionHandle', collection?.handle || '');
        sessionStorage.setItem('products', JSON.stringify(pageProducts) || '');
      }
    },
    [pageProducts],
  );

  const loadMore = () => {
    if (!collection) return;
    setLoading(true);

    getNextPage(collection?.handle, pageProducts[pageProducts.length - 1].cursor || undefined)
      .then((newProducts) => {
        if (!newProducts) return;
        setPageProducts(pageProducts.concat(newProducts));
        if (newProducts.length < pagination.products) {
          setCanLoadMore(false);
        }
      })
      .finally(() => setLoading(false));
  };

  const renderPetitionHeaderInfo = (isCollectionsSidebarExperimentEnabled: boolean) => (
    <PetitionsHeaderInfoWrapper>
      {isCollectionsSidebarExperimentEnabled ? (
        <CollectionHeaderVariant>Petitions</CollectionHeaderVariant>
      ) : (
        <PetitionCollectionH2 data-testid="collection-title">PETITIONS</PetitionCollectionH2>
      )}
      <PetitionsShopPageP1 data-testid="collection-subheader">
        A place where communities can bring creator ideas to life! 🚀
      </PetitionsShopPageP1>
      <PetitionsFAQLink
        href="https://support.makeship.com/hc/en-us/articles/29429491310477-What-is-the-difference-between-a-petition-and-a-campaign"
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => posthog.capture('petitionCollection_FAQ_link_click')}
      >
        <PetitionsFAQLinkText>Learn more about Petitions</PetitionsFAQLinkText>
      </PetitionsFAQLink>
    </PetitionsHeaderInfoWrapper>
  );

  const linkHeaderClickTracking = (handle: string) => {
    posthog.capture('collection_header_link_click', {
      collectionHandle: handle,
      is_logged_in: isUserLoggedIn(state.user),
    });
  };

  const renderLinkHeaders = () => {
    if (isPlushieCollectionHandle(handle)) {
      return (
        <CollectionHeaderVariantWrapper>
          <Link href={`/shop/${collections.plushies}`} passHref legacyBehavior>
            <StyledLink.Primary onClick={() => linkHeaderClickTracking(handle)}>
              <CollectionHeaderVariant>
                All {collections.plushies[0].toUpperCase() + collections.plushies.slice(1)}
              </CollectionHeaderVariant>
            </StyledLink.Primary>
          </Link>
          {handle !== collections.plushies && (
            <>
              <CollectionHeaderVariant> / </CollectionHeaderVariant>
              <Link href={`/shop/${handle}`} passHref legacyBehavior>
                <StyledLink.Primary>
                  <CollectionHeaderVariant>{title}</CollectionHeaderVariant>
                </StyledLink.Primary>
              </Link>
            </>
          )}
        </CollectionHeaderVariantWrapper>
      );
    }
    if (isApparelCollectionHandle(handle)) {
      return (
        <CollectionHeaderVariantWrapper>
          <Link href={`/shop/${collections.apparel}`} passHref legacyBehavior>
            <StyledLink.Primary onClick={() => linkHeaderClickTracking(handle)}>
              <CollectionHeaderVariant>
                All {collections.apparel[0].toUpperCase() + collections.apparel.slice(1)}
              </CollectionHeaderVariant>
            </StyledLink.Primary>
          </Link>
          {handle !== collections.apparel && (
            <>
              <CollectionHeaderVariant> / </CollectionHeaderVariant>
              <Link href={`/shop/${handle}`} passHref legacyBehavior>
                <StyledLink.Primary>
                  <CollectionHeaderVariant>{title}</CollectionHeaderVariant>
                </StyledLink.Primary>
              </Link>
            </>
          )}
        </CollectionHeaderVariantWrapper>
      );
    }
    if (isTopPicksCollectionHandle(handle)) {
      return (
        <CollectionHeaderVariantWrapper>
          <Link href={`/shop/${collections.topCampaigns}`} passHref legacyBehavior>
            <StyledLink.Primary onClick={() => linkHeaderClickTracking(handle)}>
              <CollectionHeaderVariant>
                {collections.topCampaigns[0].toUpperCase() + collections.topCampaigns.slice(1)} Picks
              </CollectionHeaderVariant>
            </StyledLink.Primary>
          </Link>
          {handle !== collections.topCampaigns && (
            <>
              <CollectionHeaderVariant> / </CollectionHeaderVariant>
              <Link href={`/shop/${handle}`} passHref legacyBehavior>
                <StyledLink.Primary onClick={() => linkHeaderClickTracking(handle)}>
                  <CollectionHeaderVariant>{title}</CollectionHeaderVariant>
                </StyledLink.Primary>
              </Link>
            </>
          )}
        </CollectionHeaderVariantWrapper>
      );
    }
    return (
      <CollectionHeaderVariantWrapper>
        <CollectionHeaderVariant data-testid="collection-title">{title}</CollectionHeaderVariant>
      </CollectionHeaderVariantWrapper>
    );
  };

  return (
    <>
      <Head
        title={title}
        description={collection?.description || defaultSubheading}
        url={`/shop/${handle}`}
        image={collection?.image?.transformedSrc}
      />
      <ShopPageWrapperTop>
        {isCollectionsSidebarExperimentEnabled && <div data-testid="isCollectionsSidebarExperimentEnabled" />}
        {collection?.handle === 'petitions' ? (
          renderPetitionHeaderInfo(isCollectionsSidebarExperimentEnabled)
        ) : (
          <>
            {isCollectionsSidebarExperimentEnabled ? (
              renderLinkHeaders()
            ) : (
              <ShopPageH2 data-testid="collection-title">{title}</ShopPageH2>
            )}
            {!isCollectionsSidebarExperimentEnabled && (
              <ShopPageP1 data-testid="collection-subheader">{collection?.description || defaultSubheading}</ShopPageP1>
            )}
          </>
        )}
        {/* Duplicate sidebar because in mobile it needs to be above the subcategory sidebar */}
        {isCollectionsSidebarExperimentEnabled && isMobileViewport && (
          <Sidebar handle={handle} show={showSidebar} sidebarItems={sidebarItems} />
        )}
      </ShopPageWrapperTop>
      {isCollectionsSidebarExperimentEnabled &&
        (isMaxContentViewport ? (
          <Container>
            <SubcategorySidebarWrapper>
              <SubcategorySidebar handle={handle} show={showSidebar} sidebarItems={sidebarItems} />
            </SubcategorySidebarWrapper>
          </Container>
        ) : (
          <SubcategorySidebarWrapper>
            <SubcategorySidebar handle={handle} show={showSidebar} sidebarItems={sidebarItems} />
          </SubcategorySidebarWrapper>
        ))}

      <ShopPageWrapperBottom>
        {!isCollectionsSidebarExperimentEnabled && (
          <HideFiltersRow>
            <HideFilters onClick={() => setShowSidebar(!showSidebar)}>
              <HideFiltersText data-gtm-name="show-hide">{showSidebar ? 'Hide' : 'Show'} Filters</HideFiltersText>
              <Image
                data-gtm-name="filter-span"
                src="/assets/icons/filters-icon.svg"
                width={16}
                height={16}
                quality={90}
                alt="Filters icon"
              />
            </HideFilters>
          </HideFiltersRow>
        )}

        <ShopPageContents isCollectionsSidebarExperimentEnabled={isCollectionsSidebarExperimentEnabled}>
          {(!isMobileViewport || !isCollectionsSidebarExperimentEnabled) && (
            <Sidebar handle={handle} show={showSidebar} sidebarItems={sidebarItems} />
          )}
          <ProductsDisplay
            products={
              [collections.liveCampaigns].includes(handle)
                ? filterProducts(pageProducts, false, false, true)
                : getCollectionProducts(
                    pageProducts,
                    title === 'Ending Soon',
                    [collections.readyToShip].includes(handle),
                    title === 'Petitions',
                  )
            }
            loadMore={loadMore}
            canLoadMore={canLoadMore}
            loading={loading}
            titleForTracking={title}
          />
        </ShopPageContents>
      </ShopPageWrapperBottom>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<ShopPageProps> = async (ctx) => {
  const { params } = ctx;
  const handle = params ? (params.collection as string) : '';
  const referringURL = ctx.req.headers.referer || '';
  const propsForPageLoad = await getPropsForPageLoad(ctx);

  const { experimentFlags } = propsForPageLoad.flagBag;
  const isCollectionsSidebarExperimentEnabled =
    experimentFlags[experimentKeys.collectionsSidebarExperiment] === ExperimentVariant.Variant1;

  const getSidebarItemsRequest = {
    isCollectionsSidebarExperimentEnabled,
  };

  const sidebarItemsFiltered = await getSideBarItems(getSidebarItemsRequest)
    .then((res) => res.data)
    .catch((error) => {
      logSentryError(error, 'shop_getServerSideProps', 'Failed to get sidebar items from API');
      return defaultSidebarItems;
    });

  switch (handle) {
    case 'ending-soon': {
      const products = await getEndingSoonProducts(collections.liveCampaigns);

      return {
        props: {
          title: 'Ending Soon',
          handle,
          products,
          sidebarItems: sidebarItemsFiltered,
          referringURL,
          ...propsForPageLoad,
        },
      };
    }
    case 'new': {
      const products = await getNewArrivalProducts(collections.liveCampaigns);

      return {
        props: {
          title: 'New Arrivals',
          handle,
          products,
          sidebarItems: sidebarItemsFiltered,
          referringURL,
          ...propsForPageLoad,
        },
      };
    }
    case 'ready-to-ship': {
      const numberOfProducts = 100;

      const collection = await getCollectionByHandle(handle, undefined, 1, numberOfProducts);
      if (!collection) {
        return {
          notFound: true,
        };
      }

      const productEdges = filterSuccessfulProducts(collection.products.edges);
      if (productEdges.length === 0) {
        return {
          redirect: {
            destination: `/shop/featured`,
            permanent: false,
          },
        };
      }
      // Filter products that are ready to ship based on the delivery metafield
      const readyToShipProducts = productEdges.filter((product) => isReadyToShip(product.node));

      return {
        props: {
          title: collection.title,
          handle,
          products: readyToShipProducts,
          collection,
          sidebarItems: sidebarItemsFiltered,
          referringURL,
          ...propsForPageLoad,
        },
      };
    }
    default: {
      const numberOfProducts = pagination.products;

      const collection = await getCollectionByHandle(handle, undefined, 1, numberOfProducts);
      if (!collection) {
        return {
          notFound: true,
        };
      }

      const productEdges = filterSuccessfulProducts(collection.products.edges);

      if (productEdges.length === 0) {
        return {
          redirect: {
            destination: `/shop/featured`,
            permanent: false,
          },
        };
      }

      return {
        props: {
          title: collection.title,
          handle,
          products: productEdges,
          collection,
          sidebarItems: sidebarItemsFiltered,
          referringURL,
          ...propsForPageLoad,
        },
      };
    }
  }
};

export default ShopPage;
